import {
  ArrowDownward,
  ArrowUpward,
  Edit,
  Info,
  SettingsBackupRestore,
} from '@mui/icons-material';
import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getPayInConciliationSuggestions } from 'lib/api/conciliations';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { CustomTooltip } from 'lib/designSystem/TooltipTable';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import {
  formatDate,
  formatMoney,
  validatePermissions,
  validateProducts,
} from 'lib/functions/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import EnhancedTable from '../../../../../../../../../../lib/designSystem/Table';
import EditArrearDialog from '../EditArrearDialog';

const gridSizes = {
  folio: 0.8,
  amounts: 1,
  paymentDate: 0.7,
  conciliations: 1.3,
  arrearRate: 1.5,
  saldo: 1.3,
  amountToConciliate: 1.7,
  observation: 2,
  businessName: 1.7,
};

const gridFontSize = 12;

type EditArrearDocument = {
  documentId: string;
  arrearStructure: {
    arrearType: string;
    arrearRate: number;
    arrearSegments: {
      min: number;
      max: number;
      value: number;
    }[];
  };
  hasConciliations: boolean;
} | null;

const normalizeDate = (date: number) => {
  const dateNormalized = new Date(date);
  dateNormalized.setUTCHours(12, 0, 0, 0);
  return dateNormalized.getTime();
};

const getArrearFromSegments = (document: any, payIn: any) => {
  const diffDays =
    (normalizeDate(new Date(payIn.createdAt).getTime()) -
      normalizeDate(new Date(document.paymentDate).getTime())) /
    (1000 * 60 * 60 * 24);
  const arrearSegments = document.arrearStructure.arrearSegments;
  const arrearSegment = arrearSegments.find(
    (segment: { min: number; max: number; value: number }) =>
      diffDays >= segment.min && diffDays <= segment.max,
  );
  return arrearSegment ? `${arrearSegment.value.toFixed(2)}%` : '0.00%';
};

const ConciliationHistoryTooltip = ({
  conciliationLog,
}: {
  conciliationLog: any[];
}) => {
  // Función para formatear fecha
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CL');
  };

  return (
    <div style={{ padding: 10, maxHeight: '300px', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 10 }}>
        <Typography variant="body1" fontWeight="bold">
          Historial de Conciliaciones
        </Typography>
      </div>
      <Table size="small" style={{ border: '1px solid #e0e0e0' }}>
        <TableBody>
          <TableRow style={{ backgroundColor: '#f5f5f5' }}>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Fecha recaudación
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Pago recibido
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Tipo
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Monto Total
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Documento
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Mora
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Tasa mora aplicada
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Saldo Antes
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Saldo Después
            </TableCell>
          </TableRow>
          {conciliationLog.map((log: any, index: number) => (
            <TableRow
              key={index}
              style={{
                backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9',
              }}
            >
              <TableCell style={{ textAlign: 'center' }}>
                {formatDate(log.date)}
              </TableCell>
              <TableCell style={{ textAlign: 'center' }}>
                {formatDate(log.payInDate)}
              </TableCell>
              <TableCell style={{ textAlign: 'center' }}>
                {log.typeEsp}
              </TableCell>
              <TableCell style={{ textAlign: 'right', fontWeight: 'bold' }}>
                {formatMoney(log.conciliationAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(log.conciliationDocumentAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(log.conciliationArrearAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {String(Number(log.arrearRate).toFixed(2))}%
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(
                  log.documentAmountDebtBefore + log.arrearAmountDebtBefore,
                )}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(
                  log.documentAmountDebtAfter + log.arrearAmountDebtAfter,
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const disableEditArrearStructure = (document: any, payIn: any, user: any) => {
  if (!validatePermissions(user, 'financing_update_arrear_rate')) {
    return true;
  }
  return document.conciliationLog.length > 0
    ? document.conciliationLog[document.conciliationLog.length - 1].date >=
        payIn.createdAt
    : false;
};

const disableRestoreAmount = (
  document: any,
  payIn: any,
  remainingAmount: number,
  maxAvailable: number,
) => {
  if (Math.min(remainingAmount, maxAvailable) === document.amountToConciliate) {
    return true;
  }
  return document.conciliationLog.length > 0
    ? normalizeDate(
        new Date(
          document.conciliationLog[
            document.conciliationLog.length - 1
          ].payInDate,
        ).getTime(),
      ) > normalizeDate(new Date(payIn.createdAt).getTime())
    : false;
};

const disableEditAmount = (document: any, payIn: any) => {
  return document.conciliationLog.length > 0
    ? normalizeDate(
        new Date(
          document.conciliationLog[
            document.conciliationLog.length - 1
          ].payInDate,
        ).getTime(),
      ) > normalizeDate(new Date(payIn.createdAt).getTime())
    : false;
};

const PaymentConciliationAssginationSuggested = (props: any) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const { payIn, setDocumentsSuggested, documentsSuggested } = props;

  const [loading, setLoading] = useState(false);
  const [typeSearch, setTypeSearch] = useState<string>('all');
  const [stakeholderSearch, setStakeholderSearch] = useState<string>('all');
  const [openEditArrearDialog, setOpenEditArrearDialog] = useState(false);
  const [editArrearDocument, setEditArrearDocument] =
    useState<EditArrearDocument>(null);
  // Sorting state
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | 'none'>(
    'none',
  );

  // Custom sort functions for each field
  const sortFunctions = {
    folio: (a: any, b: any) => {
      const aValue = Number(a.folio) || 0;
      const bValue = Number(b.folio) || 0;
      return aValue - bValue;
    },
    businessName: (a: any, b: any) => {
      const aValue = a.businessName || '';
      const bValue = b.businessName || '';
      return aValue.localeCompare(bValue);
    },
    amount: (a: any, b: any) => {
      const aValue = a.amount;
      const bValue = b.amount;
      return aValue - bValue;
    },
    paymentDate: (a: any, b: any) => {
      const aValue = new Date(a.paymentDate).getTime();
      const bValue = new Date(b.paymentDate).getTime();
      return aValue - bValue;
    },
    conciliations: (a: any, b: any) => {
      const aValue = a.amountConciliated || 0;
      const bValue = b.amountConciliated || 0;
      return aValue - bValue;
    },
    arrearRate: (a: any, b: any) => {
      const aValue =
        a.arrearStructure.arrearType === 'table'
          ? parseFloat(getArrearFromSegments(a, payIn).replace('%', ''))
          : parseFloat(a.arrearStructure.arrearRate.toFixed(2));
      const bValue =
        b.arrearStructure.arrearType === 'table'
          ? parseFloat(getArrearFromSegments(b, payIn).replace('%', ''))
          : parseFloat(b.arrearStructure.arrearRate.toFixed(2));
      return aValue - bValue;
    },
    saldo: (a: any, b: any) => {
      const aValue = a.totalRemainingNet;
      const bValue = b.totalRemainingNet;
      return aValue - bValue;
    },
  };

  // Handle header click for sorting
  const handleHeaderClick = (field: string) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> none -> asc
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection('none');
        setSortField(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      // New field, start with asc
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for header
  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    if (sortDirection === 'asc') return <ArrowUpward fontSize="small" />;
    if (sortDirection === 'desc') return <ArrowDownward fontSize="small" />;
    return null;
  };

  // Apply sorting to documents
  const getSortedDocuments = () => {
    if (!sortField || sortDirection === 'none') {
      return documentsSuggested;
    }
    const sorted = [...documentsSuggested].sort(
      sortFunctions[sortField as keyof typeof sortFunctions],
    );
    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  };

  const setUp = async () => {
    setLoading(true);
    try {
      const response = await getPayInConciliationSuggestions(dispatch, {
        id: payIn.id,
      });
      setDocumentsSuggested([...response.documentsProcessed]);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  const filters: Filter[] = [
    {
      label: 'Tipo',
      type: 'select',
      options: [
        { label: 'Todos', value: 'all' },
        ...[...new Set(documentsSuggested.map((doc: any) => doc.type))].map(
          (type) => ({
            label: type as string,
            value: type as string,
          }),
        ),
      ],
      value: typeSearch,
      setter: setTypeSearch,
    },
    {
      label: 'Deudor',
      type: 'select',
      options: [
        { label: 'Todos', value: 'all' },
        ...[
          ...new Set(documentsSuggested.map((doc: any) => doc.stakeholderName)),
        ].map((stakeholder) => ({
          label: stakeholder as string,
          value: stakeholder as string,
        })),
      ],
      value: stakeholderSearch,
      setter: setStakeholderSearch,
    },
  ];

  // Función para parsear el valor monetario
  const parseMoneyValue = (value: string): number => {
    const cleanValue = value.replace(/[$.]/g, '').replace(/,/g, '');
    const numericValue = Number(cleanValue);
    return isNaN(numericValue) ? 0 : numericValue;
  };

  // Función para obtener el máximo disponible para un documento
  const getMaxAvailableForDocument = (documentId: string): number => {
    // Total ya asignado a otros documentos
    const otherDocumentsTotal = documentsSuggested.reduce(
      (acc: number, doc: any) => {
        if (doc.id !== documentId) {
          return acc + (doc.amountToConciliate || 0);
        }
        return acc;
      },
      0,
    );

    // Lo que queda disponible del pago
    const availableFromPayment =
      payIn.amount - payIn.amountConciliated - otherDocumentsTotal;

    // El documento actual
    const document = documentsSuggested.find(
      (doc: any) => doc.id === documentId,
    );
    if (!document) return 0;

    // El máximo es el menor entre lo disponible del pago y lo que queda por conciliar del documento
    return Math.min(availableFromPayment, document.totalRemaining || 0);
  };

  // Handler unificado para cambios en el input
  const handleAmountChange = (value: string, documentId: string) => {
    const amount = parseMoneyValue(value);
    const maxAvailable = getMaxAvailableForDocument(documentId);

    // Validar y ajustar el monto
    const finalAmount = Math.min(Math.max(0, amount), maxAvailable);

    // Actualizar directamente el estado
    const updatedDocuments = documentsSuggested.map((doc: any) =>
      doc.id === documentId ? { ...doc, amountToConciliate: amount } : doc,
    );

    setDocumentsSuggested(updatedDocuments);
  };

  const handleObservationChange = (value: string, documentId: string) => {
    const updatedDocuments = documentsSuggested.map((doc: any) =>
      doc.id === documentId ? { ...doc, observation: value } : doc,
    );
    setDocumentsSuggested(updatedDocuments);
  };

  // Handler para el botón de restaurar
  const handleRestoreAmount = (document: any) => {
    const remainingClient = Math.round(document.totalRemainingNet);
    const remainingPayer = Math.max(
      Math.round(document.totalRemainingNet),
      document.amount - document.amountConciliated,
    );
    const remainingAmount =
      payIn.businessOriginIdentifier === document.businessIdentifier
        ? remainingClient
        : remainingPayer;

    const maxAvailable = getMaxAvailableForDocument(document.id);
    const finalAmount = Math.min(remainingAmount, maxAvailable);

    handleAmountChange(finalAmount.toString(), document.id);
  };

  // Calcular totales usando useMemo
  const { totalToConciliate, faltaPorConciliar } = useMemo(() => {
    const total = documentsSuggested.reduce((acc: number, doc: any) => {
      return acc + (doc.amountToConciliate || 0);
    }, 0);

    const falta = payIn.amount - payIn.amountConciliated - total;

    return { totalToConciliate: total, faltaPorConciliar: falta };
  }, [documentsSuggested, payIn.amount, payIn.amountConciliated]);

  // Función para formatear el valor mostrado
  const getDisplayValue = (document: any) => {
    const value = document.amountToConciliate || 0;
    return formatMoney(value);
  };

  const handleEditArrearStructure = (document: any) => {
    setEditArrearDocument({
      documentId: document.id,
      arrearStructure: document.arrearStructure,
      hasConciliations: document.conciliationLog.length > 0,
    });
    setOpenEditArrearDialog(true);
  };

  const handleSaveArrearStructure = () => {
    setEditArrearDocument(null);
    setOpenEditArrearDialog(false);
    void setUp();
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" padding="10px 0px" fontWeight={600}>
            Conciliaciones sugeridas
          </Typography>
          <LightTooltip
            title={
              <Typography variant="body1" style={{ padding: 4 }}>
                Los cálculos están basados en la fecha registrada del pago. Esta
                se encuentra arriba a la derecha
              </Typography>
            }
          >
            <Info
              fontSize="small"
              style={{ marginLeft: 5, cursor: 'pointer' }}
            />
          </LightTooltip>
        </div>
        <GlobalSearcher filters={filters} triggerSearch={async () => {}} />
      </div>
      <div
        style={{
          position: 'sticky',
          top: -20,
          backgroundColor: 'white',
          zIndex: 10,
          paddingTop: '30px',
          paddingBottom: '10px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '20px',
            marginBottom: '10px',
          }}
        >
          <Typography
            variant="body1"
            fontWeight={600}
            color={faltaPorConciliar < 0 ? 'error' : 'primary'}
          >
            Falta por conciliar: {formatMoney(faltaPorConciliar)}
          </Typography>
          <Typography fontWeight={600}>
            Total a conciliar: {formatMoney(totalToConciliate)}
          </Typography>
        </div>

        {documentsSuggested.length ? (
          <Grid container spacing={1}>
            <Grid item xs={gridSizes.folio}>
              <Typography
                variant="body1"
                fontWeight={600}
                fontSize={gridFontSize}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  userSelect: 'none',
                }}
                onClick={() => handleHeaderClick('folio')}
              >
                Folio
                {getSortIcon('folio')}
              </Typography>
            </Grid>
            <Grid item xs={gridSizes.businessName}>
              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="center"
                fontSize={gridFontSize}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  userSelect: 'none',
                }}
                onClick={() => handleHeaderClick('businessName')}
              >
                Cliente
                {getSortIcon('businessName')}
              </Typography>
            </Grid>
            <Grid item xs={gridSizes.amounts}>
              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="center"
                fontSize={gridFontSize}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  userSelect: 'none',
                }}
                onClick={() => handleHeaderClick('amount')}
              >
                Monto | Neto
                {getSortIcon('amount')}
              </Typography>
            </Grid>
            <Grid item xs={gridSizes.paymentDate}>
              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="center"
                fontSize={gridFontSize}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  userSelect: 'none',
                }}
                onClick={() => handleHeaderClick('paymentDate')}
              >
                Vencimiento
                {getSortIcon('paymentDate')}
              </Typography>
            </Grid>
            <Grid item xs={gridSizes.conciliations}>
              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="center"
                fontSize={gridFontSize}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  userSelect: 'none',
                }}
                onClick={() => handleHeaderClick('conciliations')}
              >
                Conciliaciones
                {getSortIcon('conciliations')}
              </Typography>
            </Grid>
            <Grid item xs={gridSizes.arrearRate}>
              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="center"
                fontSize={gridFontSize}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  userSelect: 'none',
                }}
                onClick={() => handleHeaderClick('arrearRate')}
              >
                Tasa mora
                {getSortIcon('arrearRate')}
              </Typography>
            </Grid>
            <Grid item xs={gridSizes.saldo}>
              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="center"
                fontSize={gridFontSize}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  userSelect: 'none',
                }}
                onClick={() => handleHeaderClick('saldo')}
              >
                Saldo
                {getSortIcon('saldo')}
              </Typography>
            </Grid>
            <Grid item xs={gridSizes.observation}>
              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="center"
                fontSize={gridFontSize}
              >
                Observación
              </Typography>
            </Grid>
            <Grid item xs={gridSizes.amountToConciliate}>
              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="right"
                fontSize={gridFontSize}
              >
                Monto a conciliar
              </Typography>
            </Grid>
          </Grid>
        ) : null}
      </div>

      {loading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 120,
          }}
        >
          <CircularProgress />
        </div>
      )}
      {!loading && (
        <div>
          {!documentsSuggested.length ? (
            <Typography variant="body1">
              No hay conciliaciones sugeridas
            </Typography>
          ) : null}
          {getSortedDocuments().map((document: any) => {
            if (
              typeSearch !== 'all' &&
              document.type !== typeSearch &&
              !document.amountToConciliate
            )
              return null;
            if (
              stakeholderSearch !== 'all' &&
              document.stakeholderName !== stakeholderSearch &&
              !document.amountToConciliate
            )
              return null;
            const tooltipTitleValues = [
              {
                name: 'Monto',
                value: formatMoney(document.amount),
              },
              {
                name: 'Fuente',
                value: document.type,
              },
              {
                name: 'Fondo',
                value: document.fundName,
              },
              {
                name: 'Cliente',
                value: document.businessName,
              },
              {
                name: 'Rut cliente',
                value: document.businessIdentifier,
              },
              {
                name: 'Deudor',
                value: document.stakeholderName,
              },
              {
                name: 'Rut deudor',
                value: document.stakeholderIdentifier,
              },
              {
                name: 'Estado',
                value: document.status,
              },
              {
                name: 'Fecha de pago',
                value: formatDate(document.paymentDate),
              },
              {
                name: 'Días vencida',
                value: document.dueDays,
              },
              {
                name: 'Anticipo',
                value: `${document.advance}%`,
              },
            ];
            const remainingClient = document.totalRemainingNet;
            const remainingPayer = Math.max(
              document.totalRemainingNet,
              document.amount - document.amountConciliated,
            );
            const remainingAmount =
              payIn.businessOriginIdentifier === document.businessIdentifier
                ? remainingClient
                : remainingPayer;

            const maxAvailable = getMaxAvailableForDocument(document.id);

            return (
              <Grid
                key={document.folio}
                container
                spacing={1}
                alignItems="center"
                style={{ marginTop: 1 }}
              >
                <Grid item xs={gridSizes.folio}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LightTooltip
                      title={CustomTooltip(
                        tooltipTitleValues,
                        'Concepto',
                        'Valor',
                      )}
                    >
                      <Info
                        style={{
                          fontSize: gridFontSize,
                          cursor: 'pointer',
                        }}
                      />
                    </LightTooltip>
                    <Typography
                      variant="body1"
                      marginLeft={0.5}
                      fontSize={gridFontSize}
                    >
                      {document.folio}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={gridSizes.businessName}>
                  <div style={{ textAlign: 'center' }}>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      fontSize={gridFontSize}
                    >
                      {document.businessName}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={gridSizes.amounts}>
                  <div style={{ textAlign: 'center' }}>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      fontSize={gridFontSize}
                    >
                      {formatMoney(document.amount)}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      fontSize={gridFontSize}
                    >
                      {formatMoney(document.amountNet)}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={gridSizes.paymentDate}>
                  <div style={{ textAlign: 'center' }}>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      fontSize={gridFontSize}
                    >
                      {formatDate(document.paymentDate)}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={gridSizes.conciliations}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                      }}
                    >
                      {document.conciliationLog &&
                      document.conciliationLog.length > 0 ? (
                        <LightTooltip
                          title={
                            <ConciliationHistoryTooltip
                              conciliationLog={document.conciliationLog}
                            />
                          }
                          placement="top"
                        >
                          <Info
                            style={{
                              cursor: 'pointer',
                              fontSize: gridFontSize,
                            }}
                          />
                        </LightTooltip>
                      ) : null}
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        fontSize={gridFontSize}
                      >
                        {formatMoney(document.amountConciliated)}
                      </Typography>
                    </div>
                    {!validateProducts(user, ['sgr']) && (
                      <>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          fontSize={gridFontSize}
                        >
                          Doc: {formatMoney(document.documentConciliation)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          fontSize={gridFontSize}
                        >
                          Mora: {formatMoney(document.arrearConciliation)}
                        </Typography>
                      </>
                    )}
                  </div>
                </Grid>
                <Grid item xs={gridSizes.arrearRate}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                      }}
                    >
                      {document.pendingAccountId ? null : (
                        <>
                          {document.arrearStructure.arrearType === 'table' ? (
                            <LightTooltip
                              title={
                                <EnhancedTable
                                  data={document.arrearStructure.arrearSegments.map(
                                    (segment: any, index: number) => {
                                      const min = segment.min;
                                      const max = segment.max;
                                      const value = segment.value;
                                      let name = `${min} a ${max} días`;
                                      if (
                                        index ===
                                        document.arrearStructure.arrearSegments
                                          .length -
                                          1
                                      ) {
                                        name = `Mayor a ${min} días`;
                                      }
                                      return {
                                        segment: name,
                                        value: `${value}%`,
                                      };
                                    },
                                  )}
                                  dataHeaders={[
                                    {
                                      key: 'segment',
                                      label: 'Tramo',
                                    },
                                    {
                                      key: 'value',
                                      label: 'Valor',
                                    },
                                  ]}
                                  hideTablePagination
                                  rowsPerPageDefault={100}
                                />
                              }
                              placement="top"
                            >
                              <Info
                                style={{
                                  cursor: 'pointer',
                                  fontSize: gridFontSize,
                                }}
                              />
                            </LightTooltip>
                          ) : null}
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            fontSize={gridFontSize}
                          >
                            {document.arrearStructure.arrearType === 'table'
                              ? `Tabla (${getArrearFromSegments(document, payIn)})`
                              : `${document.arrearStructure.arrearRate.toFixed(2)}%`}
                          </Typography>
                          <IconButton
                            onClick={() => handleEditArrearStructure(document)}
                            disabled={disableEditArrearStructure(
                              document,
                              payIn,
                              user,
                            )}
                            title={
                              document.conciliationLog.length > 0
                                ? document.conciliationLog[
                                    document.conciliationLog.length - 1
                                  ].date >= payIn.createdAt
                                  ? 'No se puede editar la mora de este documento porque tiene conciliaciones futuras'
                                  : undefined
                                : undefined
                            }
                          >
                            <Edit
                              style={{
                                cursor: 'pointer',
                                fontSize: gridFontSize,
                              }}
                            />
                          </IconButton>
                        </>
                      )}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={gridSizes.saldo}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        fontSize={gridFontSize}
                      >
                        {formatMoney(document.totalRemainingNet)}
                      </Typography>
                    </div>
                    {!validateProducts(user, ['sgr']) && (
                      <>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          fontSize={gridFontSize}
                        >
                          Doc:{' '}
                          {formatMoney(document.documentRemainingAmountNet)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          fontSize={gridFontSize}
                        >
                          Mora: {formatMoney(document.arrearAmount)}
                        </Typography>
                      </>
                    )}
                  </div>
                </Grid>
                <Grid item xs={gridSizes.observation}>
                  <TextField
                    value={document.observation}
                    placeholder="Observación (opcional)"
                    onChange={(e) =>
                      handleObservationChange(e.target.value, document.id)
                    }
                    onFocus={(e) => {
                      e.target.select();
                    }}
                    disabled={disableEditAmount(document, payIn)}
                  />
                </Grid>
                <Grid item xs={gridSizes.amountToConciliate} textAlign="right">
                  <TextField
                    value={getDisplayValue(document)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            color="primary"
                            style={{ cursor: 'pointer' }}
                            disabled={disableRestoreAmount(
                              document,
                              payIn,
                              remainingAmount,
                              maxAvailable,
                            )}
                            onClick={() => handleRestoreAmount(document)}
                            title={
                              disableEditAmount(document, payIn)
                                ? 'No se puede restaurar el monto de este documento porque tiene conciliaciones futuras'
                                : undefined
                            }
                          >
                            <SettingsBackupRestore />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      style: { textAlign: 'right' },
                    }}
                    onChange={(e) =>
                      handleAmountChange(e.target.value, document.id)
                    }
                    onFocus={(e) => {
                      e.target.select();
                    }}
                    disabled={disableEditAmount(document, payIn)}
                    title={
                      disableEditAmount(document, payIn)
                        ? 'No se puede conciliar este documento porque tiene conciliaciones futuras'
                        : undefined
                    }
                  />
                </Grid>
              </Grid>
            );
          })}
        </div>
      )}
      <EditArrearDialog
        open={openEditArrearDialog}
        setOpen={setOpenEditArrearDialog}
        document={editArrearDocument}
        onSave={handleSaveArrearStructure}
        conciliationDate={new Date(payIn.createdAt)}
      />
    </div>
  );
};

export default PaymentConciliationAssginationSuggested;
